import Autocomplete from "@mui/material/Autocomplete";
import {
  FieldWrapper,
  FormTextField,
  FormDropdownChevronIcon,
  GenericFormFieldHint,
  compactInlineAutocompleteSx,
  formAutocompleteInputSx,
  formAutocompletePaperSx,
  formInlinePlaceholderSx,
  formSelectErrorSx,
} from "styles/formStyleComponents";
import InfoIconWithTooltip from "./InfoIconWithTooltip";
import { Box } from "@mui/material";

const SingleSelectFieldComponent = ({
  name,
  label,
  placeholder,
  value,
  required,
  disabled,
  error,
  info,
  hint,
  hintId,
  options = [],
  onOpen,
  onChange,
  compact = false,
  inline = false,
  disableClearable = false,
  loading = false,
  loadingText = "Loading…",
  filterOptions,
  inputProps: htmlInputProps = {},
}) => {
  const optionValuesMatch = (a, b) => {
    if (a === b) {
      return true;
    }
    if (a == null || b == null) {
      return false;
    }
    return String(a) === String(b);
  };

  const getOptionFromValue = (val) => {
    if (val === null || val === "") return null;
    return (
      options.find(
        (opt) => optionValuesMatch(opt?.value, val) || opt === val
      ) || null
    );
  };

  const selectedOption = getOptionFromValue(value);
  const isCompactInline = compact && inline;
  const hasValue = selectedOption != null;
  const showLabel = !isCompactInline;
  const inputPlaceholder = isCompactInline
    ? !hasValue
      ? placeholder || label
      : placeholder
    : placeholder;
  const autocompleteSx = isCompactInline
    ? {
        ...compactInlineAutocompleteSx,
        ...(!!error ? formSelectErrorSx() : {}),
        ...(!hasValue && {
          "& .MuiAutocomplete-input::placeholder": (theme) => ({
            opacity: 1,
            ...formInlinePlaceholderSx(theme, { error: !!error }),
          }),
        }),
      }
    : {
        ...formAutocompleteInputSx,
        ...(!!error ? formSelectErrorSx() : {}),
      };

  const resolvedHintId =
    hintId || (hint ? `${name}-hint` : undefined);
  const hintElement =
    hint && !error ? (
      <GenericFormFieldHint id={resolvedHintId} component="div">
        {hint}
      </GenericFormFieldHint>
    ) : null;

  return (
    <FieldWrapper compact={compact} inline={inline}>
      <Autocomplete
        sx={autocompleteSx}
        disableClearable={disableClearable}
        popupIcon={<FormDropdownChevronIcon />}
        slotProps={{
          htmlInput: { autoComplete: "off", ...htmlInputProps },
          paper: { sx: formAutocompletePaperSx },
        }}
        value={selectedOption}
        onChange={(_, newValue) => {
          if (disableClearable && newValue == null) {
            return;
          }
          const optionValue = newValue?.value !== undefined ? newValue.value : newValue;
          onChange(name, optionValue);
        }}
        options={options}
        {...(filterOptions ? { filterOptions } : {})}
        disabled={disabled}
        loading={loading}
        noOptionsText={loading ? loadingText : "No options"}
        autoHighlight={false}
        onOpen={onOpen}
        getOptionLabel={(option) => option?.label || option || ""}
        isOptionEqualToValue={(option, val) => {
          if (!val) return false;
          if (option === val) return true;
          return (
            optionValuesMatch(option?.value, val?.value) ||
            optionValuesMatch(option?.value, val)
          );
        }}
        renderOption={(props, option) => {
          const isClearSelection = option?.value === -1;
          return (
            <Box
              component="li"
              {...props}
              sx={{
                ...props.sx,
                ...(isClearSelection && { color: "#6f6e6e" }),
              }}
            >
              {option?.label || option || ""}
            </Box>
          );
        }}
        renderInput={(params) => (
          <FormTextField
            {...params}
            label={showLabel ? label : undefined}
            placeholder={inputPlaceholder}
            required={required}
            size="small"
            fullWidth
            variant="outlined"
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  <InfoIconWithTooltip info={info} />
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              ...htmlInputProps,
            }}
          />
        )}
      />
      {hintElement}
    </FieldWrapper>
  );
};

export default SingleSelectFieldComponent;

